import env from './env';
import { getSystem, sendRaptorErr } from './util';
// KNBBridge 类，用于与原生交互
class KNBBridge {
  constructor() {
    // 获取 KNB Promise 实例
    this.KNBP = window.KNB?.getPromiseInstance()
    console.log('[KNBBridge] KNBP 实例:', this.KNBP);
  }

  // 打开页面
  openPage(url, query) {
    // 调用原生 openPage 方法
    this._handleNativeAction('openPage', { url, query }, () => {
      if (env.isHarmony) {
        // 鸿蒙中openPage不支持打开外部链接，先使用openLink兜底，最后使用 location.href
        console.log('[KNBBridge] openPage fallback 执行，尝试 openLink');
        this._handleNativeAction('openLink', { url }, () => {
          console.log('[KNBBridge] openLink fallback 执行，使用 location.href');
          location.href = url;
        })
      } else {
        location.href = url;
      }
    })
  }

  // 跳转页面
  jumpPage(url, query, replace = true) {
    // 调用原生 jumpPage 方法，如果不支持则使用 location.replace 或 location.href
    this._handleNativeAction('jumpPage', { url, query }, () => {
      console.log('[KNBBridge] jumpPage fallback 执行, replace:', replace);
      if (replace) {
        location.replace(url)
      } else {
        location.href = url
      }
    })
  }

  // 关闭页面
  closePage() {
    if (env.isKNB) {
      console.log('[KNBBridge] 使用 KNB.closeWebview');
      this._handleNativeAction('closeWebview', {}, () => {
        console.log('[KNBBridge] closeWebview fallback 执行');
        window.history.go(-1);
      })
    } else if (env.isMiniprogram) {
      console.log('[KNBBridge] 使用小程序 switchTab');
      window.wx?.miniProgram?.switchTab({
        url: '/index/pages/mt/mt',
      })
    } else {
      console.log('[KNBBridge] 使用 history.go(-1)');
      window.history.go(-1);
    }
  }

  // 获取屏幕亮度
  getBrightness() {
    return new Promise((resolve) => {
      if (env.isKNB) {
        this.KNBP.getBrightness()
          .then((res) => {
            resolve(res.value)
          })
          .catch(() => {
            resolve(0.8)
          })
      } else {
        resolve(0.8)
      }
    })
  }

  // 设置屏幕亮度
  setBrightness(value, success = () => {}, fail = () => {}) {
    if (env.isKNB) {
      return this.KNBP.setBrightness({value});
    } else {
      return Promise.resolve('');
    }
  }

  // 设置状态栏样式
  // @param {number} style - 状态栏字体样式：1 为白色，0 为黑色（仅 iOS 支持）
  // @param {string} backgroundColor - 状态栏背景色，rgba 色值（仅 Android 在 titans >= 11.18.7 支持）
  setStatusBarStyle(style = 0, backgroundColor = '#FFC300') {
    if (!env.isKNB) {
      return;
    }

    try {
      window.KNB.setStatusBarStyle({
        style,
        backgroundColor,
      });
    } catch (error) {
      sendRaptorErr('KNB setStatusBarStyle 调用失败', error, 'customError');
    }
  }

  // 隐藏导航栏
  // @param {number} flag - 0 表示显示，1 表示隐藏
  setNavigationBarHidden(flag = 1) {
    if (!env.isKNB) {
      return;
    }

    try {
      window.KNB.setNavigationBarHidden({
        flag,
      });
    } catch (error) {
      sendRaptorErr('KNB setNavigationBarHidden 调用失败', error, 'customError');
    }
  }

  // 设置反弹效果
  // @param {number} enabled - 1 表示允许反弹，0 表示禁止
  setBouncesEnabled(enabled = 0) {
    if (!env.isKNB) {
      return;
    }

    try {
      window.KNB.setBouncesEnabled({
        enabled,
      });
    } catch (error) {
      sendRaptorErr('KNB setBouncesEnabled 调用失败', error, 'customError');
    }
  }

  // 订阅页面出现事件
  subAppear(handle, success = () => {}, fail = () => {}) {
    this._handleNativeSubscription('appear', handle, success, fail, () => {
      // 监听 visibilitychange 事件，如果页面变为可见状态则调用 handle
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          handle()
        }
      })
      success()
    })
  }

  // 订阅页面消失事件
  subDisappear(handle, success = () => {}, fail = () => {}) {
    this._handleNativeSubscription('disappear', handle, success, fail)
  }

  // 订阅应用进入前台事件
  subForeground(handle, success = () => {}, fail = () => {}) {
    this._handleNativeSubscription('foreground', handle, success, fail)
  }

  // 订阅应用进入后台事件
  subBackground(handle, success = () => {}, fail = () => {}) {
    this._handleNativeSubscription('background', handle, success, fail)
  }

  // 订阅yoda事件
  subYodaAction(action, handle, success = () => {}, fail = () => {}) {
    this._handleYodaSubscription(action, handle, success, fail)
  }

  // 设置本地存储
  setStorage(key, value, level = 0, success = () => {}, fail = () => {}) {
    this._handleNativeAction(
      'setStorage',
      { key, value, level, success, fail },
      () => {
        // 如果不支持原生存储，则使用 localStorage
        localStorage.setItem(key, value)
        success()
      }
    )
  }

  // 获取本地存储
  getStorage(key, success = () => {}, fail = () => {}) {
    this._handleNativeAction('getStorage', { key, success, fail }, () => {
      // 如果不支持原生存储，则使用 localStorage
      success(localStorage.getItem(key))
    })
  }

  // 取消所有订阅
  unsubscribeAll() {
    ;['appear', 'disappear', 'foreground', 'background'].forEach((action) => {
      this._handleNativeAction('unsubscribe', { action })
    })
  }

  // 初始化 Pike 客户端
  initPikeClient(
    bzId,
    alias,
    extra,
    needContinuousInvoke,
    handle,
    success = () => {},
    fail = () => {}
  ) {
    this._handleNativeAction('pike.initClient', {
      bzId,
      alias,
      extra,
      needContinuousInvoke,
      handle,
      success,
      fail,
    })
  }

  // 启动 Pike 客户端
  startPikeClient(bzId, success = () => {}, fail = () => {}) {
    this._handleNativeAction('pike.startClient', { bzId, success, fail })
  }

  // 停止 Pike 客户端
  stopPikeClient(bzId, success = () => {}, fail = () => {}) {
    this._handleNativeAction('pike.stopClient', { bzId, success, fail })
  }

  // 释放 Pike 客户端
  releasePikeClient(bzId, success = () => {}, fail = () => {}) {
    this._handleNativeAction('pike.releaseClient', { bzId, success, fail })
  }

  // 获取 Pike 隧道状态
  getPikeState(bzId, success = () => {}, fail = () => {}) {
    this._handleNativeAction('pike.getTunnelState', { bzId, success, fail })
  }

  // 处理原生方法调用
  _handleNativeAction(action, params = {}, fallback) {
    if (env.isKNB) {
      // 如果支持原生方法，则调用原生方法
      window.KNB.ready(() => {
        try {
          // 创建带回调的参数，自动处理 success 和 fail
          const callbackParams = {
            ...params,
            success: (res) => {
              console.log('[KNBBridge] ' + action + ' 成功:', res);
              // 如果原参数中有 success 回调，则调用它
              if (typeof params.success === 'function') {
                params.success(res);
              }
            },
            fail: (err) => {
              console.log('[KNBBridge] ' + action + ' 失败:', err);
              sendRaptorErr(`${action}桥调用失败`, err, 'customError');
              // 原生方法失败时，调用 fallback 进行降级
              fallback?.()
            }
          };

          if (typeof window.KNB.use === 'function') {
            // 优先使用 KNB.use 方法
            console.log('[KNBBridge] 使用 KNB.use 调用:', action);
            window.KNB.use(action, callbackParams)
          } else if (typeof window.KNB[action] === 'function') {
            console.log('[KNBBridge] 调用 KNB.' + action);
            window.KNB[action](callbackParams)
          } else {
            console.warn('[KNBBridge] KNB.' + action + ' 不存在，执行 fallback');
            fallback?.()
          }
        } catch (error) {
          sendRaptorErr(`${action}桥调用失败`, error, 'customError');
          console.error('[KNBBridge] KNB action 错误:', error)
          fallback?.()
        }
      })
    } else if (fallback) {
      // 否则执行 fallback
      console.log('[KNBBridge] 非 KNB 环境，执行 fallback');
      fallback()
    } else {
      console.log('[KNBBridge] 非 KNB 环境且无 fallback');
    }
  }

  // 处理原生订阅
  _handleNativeSubscription(action, handle, success, fail, fallback) {
    this._handleNativeAction(
      'unsubscribe',
      {
        action,
        success: () =>
          this._handleNativeAction('subscribe', {
            action,
            handle,
            success,
            fail,
          }),
      },
      fallback
    )
  }

  // 处理yoda订阅，需要额外处理scope，参考 https://km.sankuai.com/collabpage/1969500743#b-f6e05630b6b34089af1967df5485b6a2
  _handleYodaSubscription(action, handle, success, fail, fallback) {
    const scope = getSystem() === 'harmony' ? 'default' : 'knb';
    this._handleNativeAction(
      'unsubscribe',
      {
        action,
        scope: scope,
        success: () =>
          this._handleNativeAction('subscribe', {
            action,
            scope: scope,
            handle,
            success,
            fail,
          }),
      },
      fallback
    )
  }
}

export default KNBBridge
